import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Phone, MapPin, Menu, Anchor, Facebook, Instagram, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { api, fileUrl } from "@/lib/api";

const NAV = [
  { name: "Home", path: "/" },
  { name: "New Inventory", path: "/inventory?condition=new" },
  { name: "Used Inventory", path: "/inventory?condition=used" },
  { name: "Brands", path: "/brands" },
  { name: "Service", path: "/service" },
  { name: "About MTM", path: "/about" },
  { name: "Contact", path: "/contact" },
];

export function Header() {
  const [settings, setSettings] = useState({});
  const [open, setOpen] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data || {})).catch(() => {}); }, []);

  const phone = settings.phone || "423-878-3520";
  const social = settings.social || {};

  return (
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200 shadow-sm" data-testid="site-header">
      {/* Utility bar */}
      <div className="hidden md:block bg-mtm-purple-deep text-white text-xs">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-9 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <a href={`tel:${phone.replace(/[^0-9]/g, "")}`} className="flex items-center gap-1.5 hover:text-mtm-teal-bright transition-colors" data-testid="utility-phone">
              <Phone size={13} /> {phone}
            </a>
            <span className="flex items-center gap-1.5 text-slate-300">
              <MapPin size={13} /> {settings.address_line || "3525 US-421 Scenic"}, {settings.city_state_zip || "Bristol, TN 37620"}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/financing" className="hover:text-mtm-teal-bright transition-colors" data-testid="utility-financing">Financing</Link>
            {social.facebook && <a href={social.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-mtm-teal-bright"><Facebook size={14} /></a>}
            {social.instagram && <a href={social.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-mtm-teal-bright"><Instagram size={14} /></a>}
          </div>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-24 lg:h-28 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center shrink-0" data-testid="logo-home-link">
          {settings.logo_path ? (
            <img src={fileUrl(settings.logo_path, 440)} alt="MTM Marine Sales & Service" className="h-[84px] lg:h-[100px] w-auto object-contain block" />
          ) : (
            <span className="flex items-center gap-2.5">
              <span className="h-12 w-12 rounded-lg bg-gradient-to-br from-mtm-purple-deep to-mtm-violet flex items-center justify-center text-white">
                <Anchor size={26} />
              </span>
              <span className="flex flex-col leading-none">
                <span className="font-heading font-extrabold text-xl text-mtm-purple-deep tracking-tight">MTM Marine</span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-mtm-teal">Sales & Service</span>
              </span>
            </span>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="primary-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.name}
              to={n.path}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-semibold rounded-md transition-colors ${isActive ? "text-mtm-purple" : "text-slate-700 hover:text-mtm-purple hover:bg-slate-50"}`}
              data-testid={`nav-${n.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.name}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <Button asChild className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full px-5" data-testid="header-shop-inventory-button">
            <Link to="/inventory">Shop Inventory</Link>
          </Button>
          <Button asChild variant="outline" className="border-mtm-teal text-mtm-teal-dark hover:bg-mtm-teal hover:text-white rounded-full px-5" data-testid="header-schedule-service-button">
            <Link to="/service">Schedule Service</Link>
          </Button>
        </div>

        {/* Mobile */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-button"><Menu /></Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-80">
            <div className="flex flex-col gap-1 mt-8">
              {NAV.map((n) => (
                <Link key={n.name} to={n.path} onClick={() => setOpen(false)}
                  className="flex items-center justify-between px-3 py-3 rounded-md text-base font-semibold text-slate-800 hover:bg-slate-50"
                  data-testid={`mobile-nav-${n.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.name} <ChevronRight size={16} className="text-slate-400" />
                </Link>
              ))}
              <Link to="/financing" onClick={() => setOpen(false)} className="px-3 py-3 rounded-md text-base font-semibold text-slate-800 hover:bg-slate-50">Financing</Link>
              <Button asChild className="mt-4 bg-mtm-purple text-white rounded-full"><Link to="/inventory" onClick={() => setOpen(false)}>Shop Inventory</Link></Button>
              <Button asChild variant="outline" className="border-mtm-teal text-mtm-teal-dark rounded-full"><Link to="/service" onClick={() => setOpen(false)}>Schedule Service</Link></Button>
              <a href={`tel:${phone.replace(/[^0-9]/g, "")}`} className="mt-4 flex items-center gap-2 px-3 text-mtm-purple font-semibold"><Phone size={16} /> {phone}</a>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
